import { Component, ElementRef, EventEmitter, forwardRef, HostListener, Input, OnDestroy, Output, ViewChild } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { ModalController } from '@ionic/angular';
import { TranslateService } from '@ngx-translate/core';
import { ToastService } from 'src/app/services/toast.service';
import { AppConstants } from 'src/app/utilities/constant';
import { UserAuthService } from 'src/app/services/user-auth/user-auth.service';
import { AiFieldContextType, AiWritingService } from 'src/app/services/ai-writing/ai-writing.service';
import { AiWritingHistoryService, HistoryEntry } from 'src/app/services/ai-writing/ai-writing-history.service';
import { AiAdjustBarComponent } from '../ai-adjust-bar/ai-adjust-bar.component';
import { AiHistoryModalComponent } from 'src/app/components/ai-history-modal/ai-history-modal.component';

// Shared wrapper for every AI-writing field in the app, so the ring/veil/reveal
// chrome and the proofread/rewrite/undo/redo logic exist in one place instead
// of once per call site. An `ion-textarea`/`ion-input` is owned internally; a
// single opaque `historyKey` string identifies this field instance, and
// `fieldContextType` (the backend FIELD_CONTEXTS key) both drives the prompt
// and gates whether AI is enabled at all — omit it and this behaves like a
// plain field.
//
// Implements ControlValueAccessor so call sites keep using
// formControlName="..." or [(ngModel)] name="..." exactly as they did with
// the ion-textarea/ion-input/plain textarea this replaces — that's what
// keeps existing `required` and custom validators working with minimal
// call-site churn.
const FAILURE_MESSAGES: Record<'proofread' | 'rewrite' | 'adjust', string> = {
  proofread: 'Proofread failed. Please try again.',
  rewrite: 'Rewrite failed. Please try again.',
  adjust: "Couldn't apply that change. Please try again.",
};

@Component({
  selector: 'app-ai-writing-field-shell',
  templateUrl: './ai-writing-field-shell.component.html',
  styleUrls: ['./ai-writing-field-shell.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => AiWritingFieldShellComponent),
      multi: true,
    },
  ],
})
export class AiWritingFieldShellComponent implements ControlValueAccessor, OnDestroy {
  // Backend FIELD_CONTEXTS key (app/api/ai-writing/helpers.ts). Omitting
  // this disables the AI toolbar entirely — the field renders as a plain
  // textarea/input, nothing more. This is how edit-add-ons.component opts
  // AI in only for its hazard/measure usage and leaves other add-on types
  // (e.g. machine attributes) untouched.
  @Input() fieldContextType?: AiFieldContextType;
  // Unique per field INSTANCE — looped fields (control measures, new-machine
  // rows) must fold their row id/index in, e.g. `control-measure::${id}`.
  @Input() historyKey?: string;
  @Input() placeholder = '';
  @Input() maxlength?: number;
  @Input() rows = 4;
  @Input() multiline = true;
  @Input() density: 'full' | 'compact' = 'full';
  @Input() isReadonly = false;
  // When true, Proofread/Rewrite stay hidden on focus until the user
  // explicitly reveals them via the sparkle trigger. Undo/Redo/History
  // (when there's history) are unaffected. Resets on blur.
  @Input() manualAiTools = false;
  // AI writing is permission-gated. The user must have the Proofreading
  // permission in their backend RBAC list for the toolbar/actions to appear.
  // A call site can still override per-field with [aiDisabled]="true".
  @Input() aiDisabled = false;

  // Fires on blur-with-a-change AND immediately after any AI op lands
  // (proofread/rewrite/undo/redo/restore), since those bypass blur. Call
  // sites bind their existing save method to this, same as they already
  // bind (ionBlur)/(blur) today.
  @Output() commit = new EventEmitter<void>();

  @ViewChild(AiAdjustBarComponent) adjustBar?: AiAdjustBarComponent;

  value = '';
  disabledState = false;
  focused = false;
  revealing = false;
  aiToolsRevealed = false;
  proofreadLoading = false;
  rewriteLoading = false;
  adjustLoading = false;

  // ——— Adjust bar ———
  // Expands in place under the field rather than opening a modal: a modal hides
  // the very sentence being steered, and costs a dismiss tap on a device where
  // every tap is the thing we are trying to save.
  //
  // The bar holds ONE control — a text box. No preset chips: a row ordered by
  // what you used most moves position as you use it, so it has to be read every
  // time, and `/` then Enter is the same two keys forever.
  adjustOpen = false;
  /** True after a successful run, cleared on the next. Drives the result tab
   *  rather than a toast, which would time out and cover what it describes. */
  applied = false;
  /** The skill that produced it, when one did. Named because it is a slug the
   *  consultant authored and can recognise. Free-typed instructions leave this
   *  empty and the tab just says "Adjusted" — echoing raw input back ("Adjusted
   *  — uhh,") reports nothing and reads as a bug. The instruction itself is on
   *  the version-history entry, which is where it belongs. */
  appliedSkillId = '';
  /** Inline, not a toast: the instruction stays in the box so retry is one tap.
   *  Making someone retype after a failure is the fastest way to lose them. */
  adjustError = '';


  private focusValue = '';
  private aiBusyCount = 0;
  private revealTimer: any = null;
  private phraseTimer: any = null;

  private static readonly LOADING_PHRASES = [
    'Reading it twice…',
    'Sharpening the wording…',
    'Untangling that sentence…',
    "Dotting the i's…",
    'Trimming the fluff…',
    'Choosing stronger words…',
    "Crossing the t's…",
    'Consulting the style guide…',
  ];
  loadingPhrase = AiWritingFieldShellComponent.LOADING_PHRASES[0];

  private onChange: (value: string) => void = () => {};
  private onTouched: () => void = () => {};

  constructor(
    private aiWritingService: AiWritingService,
    private aiHistory: AiWritingHistoryService,
    private elementRef: ElementRef<HTMLElement>,
    private userAuthService: UserAuthService,
    private toastService: ToastService,
    private translate: TranslateService,
    private modalController: ModalController,
  ) {}

  ngOnDestroy(): void {
    if (this.phraseTimer) clearInterval(this.phraseTimer);
    if (this.revealTimer) clearTimeout(this.revealTimer);
  }

  // ——— ControlValueAccessor ———
  writeValue(value: string): void {
    this.value = value ?? '';
  }
  registerOnChange(fn: (value: string) => void): void {
    this.onChange = fn;
  }
  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }
  setDisabledState(isDisabled: boolean): void {
    this.disabledState = isDisabled;
  }

  get aiEnabled(): boolean {
    return !!this.fieldContextType && !!this.historyKey && !this.aiDisabled && this.userAuthService.hasPermission('PROOFREADING');
  }

  get hasText(): boolean {
    return !!this.value?.trim();
  }

  get hasHistory(): boolean {
    return this.aiEnabled && this.aiHistory.hasHistory(this.historyKey!);
  }

  get canUndo(): boolean {
    return this.aiEnabled && this.aiHistory.canUndo(this.historyKey!);
  }

  get canRedo(): boolean {
    return this.aiEnabled && this.aiHistory.canRedo(this.historyKey!);
  }

  // Drives the border-beam ring — stays lit through the reveal sweep so the
  // whole sequence reads as one continuous moment.
  get isBusy(): boolean {
    return this.proofreadLoading || this.rewriteLoading || this.adjustLoading || this.revealing;
  }

  // Drives the opaque veil — hides the instant the reveal sweep takes over,
  // since the sweep runs on the real field text, not a covering overlay.
  get showVeil(): boolean {
    return (this.proofreadLoading || this.rewriteLoading || this.adjustLoading) && !this.revealing;
  }

  get showToolbar(): boolean {
    // `adjustOpen` keeps the row alive while the bar is open: typing an
    // instruction moves focus off the field, and without this the toolbar — and
    // the bar hanging off it — would vanish the moment the user started typing.
    return (
      this.aiEnabled &&
      !this.isReadonly &&
      !this.disabledState &&
      (this.focused || this.adjustOpen) &&
      (this.hasText || this.hasHistory)
    );
  }

  // Proofread/Rewrite are visible unless this field opted into manual mode
  // and the user hasn't revealed them yet.
  get showAiActions(): boolean {
    return !this.manualAiTools || this.aiToolsRevealed;
  }

  // The sparkle trigger only exists in manual mode, while there's text to act
  // on and the actions are still hidden.
  get showAiTrigger(): boolean {
    return this.aiEnabled && this.manualAiTools && !this.aiToolsRevealed && this.hasText;
  }

  toggleAiTools(): void {
    this.aiToolsRevealed = !this.aiToolsRevealed;
  }

  onInput(newValue: string | null | undefined): void {
    const next = newValue ?? '';
    if (next === this.value) return;
    this.value = next;
    this.onChange(next);
  }

  onFocus(): void {
    this.focused = true;
    this.focusValue = this.value ?? '';
  }

  onBlur(): void {
    this.focused = false;
    this.aiToolsRevealed = false;
    this.onTouched();
    if ((this.value ?? '') !== this.focusValue) {
      this.commit.emit();
    }
  }

  onProofread(): void {
    this.run('proofread');
  }

  onRewrite(): void {
    this.run('rewrite');
  }

  onUndo(): void {
    this.applyHistory((key) => this.aiHistory.undo(key));
  }

  onRedo(): void {
    this.applyHistory((key) => this.aiHistory.redo(key));
  }

  async onOpenHistory(): Promise<void> {
    if (!this.aiEnabled) return;
    const key = this.historyKey!;
    this.aiHistory.syncCurrent(key, this.value ?? '');
    const history = this.aiHistory.get(key);
    if (!history || history.entries.length <= 1) return;
    const modal = await this.modalController.create({
      component: AiHistoryModalComponent,
      cssClass: 'ai-history-modal',
      componentProps: {
        entries: history.entries,
        index: history.index,
        fieldLabel: this.placeholder || '',
      },
    });
    await modal.present();
    const result = await modal.onDidDismiss();
    const restoreIndex = result?.data?.restoreIndex;
    if (typeof restoreIndex === 'number') {
      this.applyHistory((k) => this.aiHistory.restoreTo(k, restoreIndex));
    }
  }

  // ——— Adjust ———

  /** The bar owns the box, the slash menu and the styles; the host owns the
   *  model call, so this is the whole seam between them. */
  onAdjustApply(e: { instruction: string; skillId: string }): void {
    this.run('adjust', e.instruction, e.skillId);
  }

  onAdjust(): void {
    this.adjustOpen ? this.closeAdjust() : this.openAdjust();
  }

  openAdjust(): void {
    if (!this.aiEnabled || this.isReadonly || this.disabledState) return;
    this.adjustOpen = true;
    this.adjustError = '';
    this.adjustBar?.focusInput();
  }

  closeAdjust(): void {
    this.adjustOpen = false;
    this.adjustError = '';
    this.clearResult();
    this.adjustBar?.reset();
  }

  clearResult(): void {
    this.applied = false;
    this.appliedSkillId = '';
  }

  // Tapping anywhere outside the field closes the bar. Mirrors the toolbar's own
  // overflow menu, so the two dismiss the same way.
  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    if (!this.adjustOpen) return;
    if (!this.elementRef.nativeElement.contains(event.target as Node)) this.closeAdjust();
  }

  private applyHistory(mutate: (key: string) => HistoryEntry | null): void {
    if (!this.aiEnabled || this.isReadonly || this.disabledState) return;
    const key = this.historyKey!;
    this.aiHistory.syncCurrent(key, this.value ?? '');
    const entry = mutate(key);
    if (!entry) return;
    this.value = entry.text;
    this.onChange(this.value);
    this.startReveal();
    this.commit.emit();
  }

  // `instruction`/`label` are only passed for 'adjust'. `instruction` is what
  // reaches the model; `label` is the id of the skill that supplied it, or '' when
  // the consultant typed it themselves.
  private run(tool: 'proofread' | 'rewrite' | 'adjust', instruction?: string, label?: string): void {
    if (!this.aiEnabled) return;
    if (!this.userAuthService.checkUserExistingSync()) {
      this.toastService.presentToast(
        this.translate.instant('Please sign in to Blueprint to use AI writing tools.'),
        2500,
        AppConstants.WARNING_SNACKBAR,
        'top',
      );
      return;
    }
    if (this.isLoading(tool)) return;
    const original = (this.value || '').trim();
    if (!original) return;
    if (tool === 'adjust' && !(instruction || '').trim()) return;

    const key = this.historyKey!;
    this.aiHistory.ensureSeed(key, this.value || '');
    this.setLoading(tool, true);
    this.beginAiBusy();

    this.aiWritingService
      .transform(tool, original, { type: this.fieldContextType! }, instruction)
      .then((result: string) => {
        this.value = result;
        this.onChange(result);
        this.aiHistory.record(key, {
          text: result,
          action: tool,
          at: Date.now(),
          // Stored so version history can show the result against what was
          // actually asked for — a result alone doesn't say why it looks that way.
          ...(tool === 'adjust' ? { prompt: instruction } : {}),
        });
        if (tool === 'adjust') {
          this.applied = true;
          this.appliedSkillId = label || '';
        }
        this.startReveal();
        this.commit.emit();
      })
      .catch((err: any) => {
        const message = err?.error?.error || this.translate.instant(FAILURE_MESSAGES[tool]);
        // Adjust reports inline instead of via toast: the bar is already open and
        // holding the instruction, so the error belongs next to the retry.
        if (tool === 'adjust') this.adjustError = message;
        else this.toastService.presentToast(message, 2500, AppConstants.WARNING_SNACKBAR, 'top');
      })
      .finally(() => {
        this.setLoading(tool, false);
        this.endAiBusy();
      });
  }

  private isLoading(tool: 'proofread' | 'rewrite' | 'adjust'): boolean {
    if (tool === 'proofread') return this.proofreadLoading;
    if (tool === 'rewrite') return this.rewriteLoading;
    return this.adjustLoading;
  }

  private setLoading(tool: 'proofread' | 'rewrite' | 'adjust', value: boolean): void {
    if (tool === 'proofread') this.proofreadLoading = value;
    else if (tool === 'rewrite') this.rewriteLoading = value;
    else this.adjustLoading = value;
  }

  private startReveal(): void {
    this.revealing = true;
    if (this.revealTimer) clearTimeout(this.revealTimer);
    // Matches .ai-field-revealing's animation-duration (1.35s).
    this.revealTimer = setTimeout(() => {
      this.revealing = false;
    }, 1350);
  }

  private beginAiBusy(): void {
    this.aiBusyCount++;
    if (this.aiBusyCount === 1) {
      let i = 0;
      this.loadingPhrase = this.translate.instant(AiWritingFieldShellComponent.LOADING_PHRASES[0]);
      this.phraseTimer = setInterval(() => {
        i = (i + 1) % AiWritingFieldShellComponent.LOADING_PHRASES.length;
        this.loadingPhrase = this.translate.instant(AiWritingFieldShellComponent.LOADING_PHRASES[i]);
      }, 1600);
    }
  }

  private endAiBusy(): void {
    this.aiBusyCount = Math.max(0, this.aiBusyCount - 1);
    if (this.aiBusyCount === 0 && this.phraseTimer) {
      clearInterval(this.phraseTimer);
      this.phraseTimer = null;
    }
  }
}
